// NewPatientForm.js - Gemensamt formulär för ny patient (alla ingångar).
//
// Klinik med SPAR ("SPAR först"): personnummer → "Hämta från SPAR" → namn och
// adress visas skrivskyddat från folkbokföringen, telefon/e-post redigerbart.
// Manuell registrering bara som reservväg med orsak (loggas i backend).
// Klinik utan SPAR: vanligt manuellt formulär.
//
// Typ av id (Spår P3): personnummer/samordningsnummer normaliseras i backend;
// reservnummer/utländskt/annat är valfri text utan SPAR-uppslag (vid SPAR-klinik
// registreras de med orsaken reserve_number).
//
// Renderar ingen <form> (används även inuti bokningsmodalens formulär) —
// Enter hanteras här och skickar aldrig det yttre formuläret.
import React, { useState, useEffect, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPatient, folkbokforingLookup } from '../Calendar/calendarApi';
import apiClient from '../../apiClient';
import { ID_TYPES, isPnrIdType, idTypeLabel } from '../../utils/personnummer';
import './NewPatientForm.css';

export const MANUAL_REASONS = [
  { value: 'not_in_spar', label: 'Finns inte i SPAR' },
  { value: 'protected_identity', label: 'Skyddad identitet' },
  { value: 'spar_unavailable', label: 'SPAR ej nåbart' },
  { value: 'reserve_number', label: 'Reservnummer/utländsk patient' },
];

const EMPTY_FIELDS = {
  firstname: '', lastname: '', phone: '', email: '',
  address: '', street: '', postal_code: '', city: ''
};

const NewPatientForm = ({
  onCreated,             // (patient) => void|Promise — patienten är skapad
  onCancel,              // valfri: visar Avbryt
  onExisting,            // valfri: (patient) => void — finns redan på kliniken
  existingLabel = 'Öppna patienten',
  submitLabel = 'Skapa patient',
  beforeCreate,          // valfri: () => bool | Promise<bool> — t.ex. omfångsval i serie
  showAddress = true,    // manuella adressfält (av i kompakta inline-formulär)
  compact = false,
  autoFocus = true,
}) => {
  const navigate = useNavigate();
  const uid = useId();
  const fid = (name) => `${uid}-${name}`;
  // null = laddar; SPAR-uppslag bara för klinik med aktivering
  const [sparAvailable, setSparAvailable] = useState(null);
  const [pnr, setPnr] = useState('');
  const [idType, setIdType] = useState('personnummer');
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [sparResult, setSparResult] = useState(null);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [existing, setExisting] = useState(null);
  const [manual, setManual] = useState(false);
  const [manualReason, setManualReason] = useState('');
  const [confirmDeregistered, setConfirmDeregistered] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    apiClient.get('/api/journal/clinic/settings')
      .then(res => { if (alive) setSparAvailable(!!res.data.spar_lookup_available); })
      .catch(() => { if (alive) setSparAvailable(false); });
    return () => { alive = false; };
  }, []);

  const setField = (name) => (e) => {
    const value = e.target.value;
    setFields(prev => ({ ...prev, [name]: value }));
  };

  const resetLookup = () => {
    setSparResult(null);
    setLookupFailed(false);
    setExisting(null);
    setConfirmDeregistered(false);
  };

  const handlePnrChange = (e) => {
    setPnr(e.target.value);
    resetLookup();
    setError(null);
  };

  const isPnr = isPnrIdType(idType);
  const idLabel = idTypeLabel(idType);
  // Reservnummer/utländskt id: aldrig SPAR — direkt till manuella fält
  const sparFlow = sparAvailable && !manual && isPnr;

  const handleIdTypeChange = (e) => {
    const next = e.target.value;
    setIdType(next);
    resetLookup();
    setError(null);
    if (!isPnrIdType(next)) {
      setManual(false);
      setManualReason('');
    }
  };

  const handleLookup = async () => {
    if (lookingUp || saving) return;  // ett uppslag i taget — varje uppslag kostar
    if (!pnr.trim()) {
      setError('Ange personnummer först');
      return;
    }
    try {
      setLookingUp(true);
      setError(null);
      resetLookup();
      const res = await folkbokforingLookup(pnr);
      if (!res.protected && !(res.firstname && res.lastname)) {
        // Ofullständigt svar (inget namn, inget skydd) — registrera manuellt
        setLookupFailed(true);
        setError('SPAR lämnade inget namn — registrera manuellt');
        return;
      }
      setSparResult(res);
    } catch (err) {
      const status = err.response?.status;
      const data = err.response?.data || {};
      if (status === 409 && data.patient) {
        setExisting(data.patient);
      } else {
        // 503 = ingen träff/SPAR nere, 429 = gränsen nådd → reservvägen
        setLookupFailed(status === 503 || status === 429 || !status);
        setError(data.error || 'SPAR-uppslaget misslyckades');
      }
    } finally {
      setLookingUp(false);
    }
  };

  const startManual = (reason) => {
    setManual(true);
    setManualReason(reason || '');
    setSparResult(null);
    setExisting(null);
    setError(null);
  };

  const backToSpar = () => {
    setManual(false);
    setManualReason('');
    resetLookup();
    setError(null);
  };

  const sparProtected = !!sparResult?.protected;
  const sparDeregistered = !!sparResult?.deregistered;
  const sparHasNames = !!(sparResult?.firstname && sparResult?.lastname);
  // Namn skrivs av personalen: manuellt, klinik utan SPAR, eller när SPAR inte
  // lämnar något namn (skyddad identitet)
  const staffNames = !sparFlow || !sparHasNames;
  const canSubmit = sparAvailable === false || manual || !!sparResult || !isPnr;

  const handleCreate = async () => {
    if (saving || lookingUp) return;
    if (!pnr.trim()) {
      setError(`${idLabel} krävs`);
      return;
    }
    if (staffNames && (!fields.firstname.trim() || !fields.lastname.trim())) {
      setError('Förnamn och efternamn krävs');
      return;
    }
    if (manual && !manualReason) {
      setError('Ange orsak till manuell registrering');
      return;
    }
    if (sparDeregistered && !confirmDeregistered) {
      setError('Bekräfta att patienten ska skapas trots avregistreringen');
      return;
    }
    if (beforeCreate && !(await beforeCreate())) return;

    const payload = { personnummer: pnr, id_type: idType, phone: fields.phone, email: fields.email };
    if (staffNames) {
      payload.firstname = fields.firstname;
      payload.lastname = fields.lastname;
    }
    if (!sparFlow) {
      if (showAddress && manualReason !== 'protected_identity') {
        Object.assign(payload, {
          address: fields.address, street: fields.street,
          postal_code: fields.postal_code, city: fields.city
        });
      }
      if (manual) payload.manual_reason = manualReason;
      else if (sparAvailable && !isPnr) payload.manual_reason = 'reserve_number';
    }
    if (sparDeregistered) payload.confirm_deregistered = true;

    let patient;
    try {
      setSaving(true);
      setError(null);
      patient = await createPatient(payload);
    } catch (err) {
      const data = err.response?.data || {};
      if (err.response?.status === 409 && data.patient) {
        setExisting(data.patient);
      } else if (data.code === 'spar_required') {
        // SPAR-svaret har gått ut (10 min), eller klinikinställningen kunde inte
        // läsas — tillbaka till SPAR-flödet så att uppslaget/reservvägen finns
        setSparAvailable(true);
        setManual(false);
        resetLookup();
        setError(manual ? (data.error || 'Ange orsak till manuell registrering')
          : 'Hämta uppgifterna från SPAR först (SPAR-svaret kan ha gått ut)');
      } else {
        setError(data.error || 'Kunde inte skapa patient');
      }
      setSaving(false);
      return;
    }
    setSaving(false);
    setPnr('');
    setIdType('personnummer');
    setFields(EMPTY_FIELDS);
    resetLookup();
    setManual(false);
    setManualReason('');
    await onCreated?.(patient);
  };

  const handleExisting = () => {
    if (onExisting) onExisting(existing);
    else navigate(`/patients/${existing.patient_id}`);
  };

  // Enter: hämta från SPAR i steg 1, annars skapa. Aldrig det yttre formuläret.
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter' || e.target.tagName !== 'INPUT') return;
    e.preventDefault();
    e.stopPropagation();
    if (saving || lookingUp) return;
    if (sparFlow && !sparResult) handleLookup();
    else if (canSubmit) handleCreate();
  };

  if (sparAvailable === null) {
    return <div className="npf npf-loading"><i className="fas fa-spinner fa-spin"></i> Laddar...</div>;
  }

  const sparAddress = sparResult && !sparProtected && sparResult.street
    ? `${sparResult.street}, ${[sparResult.postal_code, sparResult.city].filter(Boolean).join(' ')}`
    : null;

  return (
    <div className={`npf ${compact ? 'npf-compact' : ''}`} onKeyDown={handleKeyDown}>
      {error && <div className="npf-error">{error}</div>}

      <div className="npf-group">
        <label htmlFor={fid('idtype')}>Typ av id</label>
        <select id={fid('idtype')} value={idType} onChange={handleIdTypeChange}>
          {ID_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </div>

      <div className="npf-group">
        <label htmlFor={fid('pnr')}>{idLabel} *</label>
        <div className="npf-pnr-row">
          <input
            id={fid('pnr')}
            type="text"
            placeholder={isPnr ? 'ÅÅMMDD-XXXX' : 'Bokstäver, siffror och bindestreck'}
            maxLength={isPnr ? undefined : 32}
            value={pnr}
            onChange={handlePnrChange}
            autoFocus={autoFocus}
          />
          {sparFlow && (
            <button
              type="button"
              className="btn btn-primary npf-lookup-btn"
              onClick={handleLookup}
              disabled={lookingUp}
              title="Hämta namn och adress från folkbokföringen (SPAR)"
            >
              {lookingUp ? 'Hämtar...' : 'Hämta från SPAR'}
            </button>
          )}
        </div>
      </div>

      {existing && (
        <div className="npf-notice">
          <i className="fas fa-user-check"></i> Patienten finns redan: <strong>{existing.fullname}</strong>
          <button type="button" className="npf-link" onClick={handleExisting}>
            {existingLabel}
          </button>
        </div>
      )}

      {sparFlow && !sparResult && !existing && (
        <div className="npf-links">
          {lookupFailed && (
            <button type="button" className="npf-link" onClick={() => startManual(
              /ingen träff|inget namn/i.test(error || '') ? 'not_in_spar' : 'spar_unavailable')}>
              Kan inte hämtas från SPAR — registrera manuellt
            </button>
          )}
          <button type="button" className="npf-link npf-link-muted"
                  onClick={() => handleIdTypeChange({ target: { value: 'reservnummer' } })}>
            Reservnummer eller utländsk patient?
          </button>
        </div>
      )}

      {sparFlow && sparResult && (
        <div className="npf-spar">
          <div className="npf-spar-title"><i className="fas fa-id-card"></i> Från folkbokföringen (SPAR)</div>
          {sparProtected && (
            <div className="npf-warning">
              <i className="fas fa-user-shield"></i> Skyddade personuppgifter — ingen adress sparas
              och inga brev skickas.{!sparHasNames && ' SPAR lämnar inget namn — fyll i namnet nedan.'}
            </div>
          )}
          {sparHasNames && (
            <dl className="npf-readonly">
              <dt>Namn</dt><dd>{sparResult.firstname} {sparResult.lastname}</dd>
              {sparResult.tilltalsnamn && (<><dt>Tilltalsnamn</dt><dd>{sparResult.tilltalsnamn}</dd></>)}
              {!sparProtected && (<><dt>Adress</dt><dd>{sparAddress || 'Ingen adress i folkbokföringen'}</dd></>)}
            </dl>
          )}
          {sparDeregistered && (
            <div className="npf-warning">
              <i className="fas fa-exclamation-triangle"></i> Avregistrerad i folkbokföringen
              ({sparResult.deregistration_reason || 'okänd orsak'}
              {sparResult.deregistration_date ? `, ${sparResult.deregistration_date}` : ''}) —
              påminnelser och kallelser skickas inte.
              <label className="npf-check">
                <input
                  type="checkbox"
                  checked={confirmDeregistered}
                  onChange={(e) => setConfirmDeregistered(e.target.checked)}
                />
                Skapa patienten ändå
              </label>
            </div>
          )}
        </div>
      )}

      {manual && (
        <div className="npf-manual">
          <div className="npf-group">
            <label htmlFor={fid('reason')}>Orsak till manuell registrering *</label>
            <select id={fid('reason')} value={manualReason} onChange={(e) => setManualReason(e.target.value)}>
              <option value="">Välj orsak...</option>
              {/* Reservnummer/utländskt väljs som typ av id ovan i stället */}
              {MANUAL_REASONS.filter(r => r.value !== 'reserve_number' || manualReason === 'reserve_number')
                .map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <button type="button" className="npf-link npf-link-muted" onClick={backToSpar}>
            Tillbaka till SPAR-uppslag
          </button>
        </div>
      )}

      {canSubmit && (<>
        {staffNames && (
          <div className="npf-row">
            <div className="npf-group">
              <label htmlFor={fid('firstname')}>Förnamn *</label>
              <input type="text" id={fid('firstname')} value={fields.firstname} onChange={setField('firstname')} />
            </div>
            <div className="npf-group">
              <label htmlFor={fid('lastname')}>Efternamn *</label>
              <input type="text" id={fid('lastname')} value={fields.lastname} onChange={setField('lastname')} />
            </div>
          </div>
        )}

        <div className="npf-row">
          <div className="npf-group">
            <label htmlFor={fid('phone')}>Telefon</label>
            <input type="tel" id={fid('phone')} value={fields.phone} onChange={setField('phone')} />
          </div>
          <div className="npf-group">
            <label htmlFor={fid('email')}>E-post</label>
            <input type="email" id={fid('email')} value={fields.email} onChange={setField('email')} />
          </div>
        </div>

        {/* Manuella adressfält — aldrig vid SPAR-uppgifter eller skyddad identitet */}
        {!sparFlow && showAddress && manualReason !== 'protected_identity' && (<>
          <div className="npf-group">
            <label htmlFor={fid('address')}>Adress (fritext)</label>
            <input type="text" id={fid('address')} value={fields.address} onChange={setField('address')} />
          </div>
          <div className="npf-group">
            <label htmlFor={fid('street')}>Postadress (för brevkallelser)</label>
            <input type="text" placeholder="Gatuadress" id={fid('street')} value={fields.street} onChange={setField('street')} />
          </div>
          <div className="npf-row">
            <div className="npf-group">
              <label htmlFor={fid('postal_code')}>Postnummer</label>
              <input type="text" id={fid('postal_code')} value={fields.postal_code} onChange={setField('postal_code')} />
            </div>
            <div className="npf-group">
              <label htmlFor={fid('city')}>Ort</label>
              <input type="text" id={fid('city')} value={fields.city} onChange={setField('city')} />
            </div>
          </div>
        </>)}
      </>)}

      <div className="npf-actions">
        {onCancel && (
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
            Avbryt
          </button>
        )}
        {canSubmit && (
          <button type="button" className="btn btn-primary" onClick={handleCreate} disabled={saving}>
            {saving ? <><i className="fas fa-spinner fa-spin"></i> Skapar...</> : submitLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default NewPatientForm;
